import React, { useEffect, useState, useContext } from "react";
import Page from "components/ui/Page";
import Header from "components/ui/Header";
import { Box, Flex, Grid, Link, Spinner, Button, Badge } from "theme-ui";
import { BackArrowIcon, CheckMarkIcon, PlusIcon, ProductsIcon, TrashIcon, PencilIcon } from "components/svg";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { Tab, Tabs } from "components/ui/Tabs";
import { themevals } from "theme/themevals";
import BasicButton from "components/forms/BasicButton";
import H5 from "components/typography/H5";
import Subtext from "components/typography/Subtext";
import TextInput from "components/forms/TextInput";
import { getProject } from "api/Projects";
import Separator from "components/ui/Separator";
import SelectInput from "components/forms/SelectInput";
import MultiText from "components/forms/MultiText";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "components/ui/Modal";
import { ModalIcon, ModalSubtitle, ModalTitle, ModalTitleWrapper } from "components/ui/Header/styled";
import P from "components/typography/P";
import SearchField from "components/forms/SearchField";
import Checkbox from "components/forms/Checkbox";
import { getProducts } from "api/Products";
import { getServices } from "api/Services";
import { getAllDepots, getDepot } from "api/Providers/Depots";
import { getQuote } from "api/Quotes";
import { createColumnHelper } from "@tanstack/react-table";
import TableBasic from "components/ui/Table";
import Span from "components/typography/Span";
import { getProduct } from "api/Products";
import { getService } from "api/Services";
import { createInvoice } from "api/Invoices";
import UserContext from "context/User";
import { useToast } from "components/toast";
import { trackActivity } from "api/ActivityTracker";

const ProjectInvoiceCreate = () => {
  const params = useParams();
  const { id, quoteId } = params;
  // Session 65 item 7b: when the add-invoice flow is opened from a
  // maintenance work order (WhatsNext "Add invoice" / the project-tab
  // redirect), it carries `?returnToWo=<wo.id>`. On create and on the back
  // link we then return to the WO invoice tab instead of the project /
  // the new invoice record, so the operator stays in the case file (RC5).
  // Absent the param, the normal project-opened flow is unchanged.
  const [searchParams] = useSearchParams();
  const returnToWo = searchParams.get("returnToWo");
  const backHref = returnToWo
    ? `/maintenance/work-orders/${returnToWo}?tab=invoice`
    : `/projects/${params.id}`;
  const [project, setProject] = useState<any>(null);
  const [items, setItems] = useState<any[]>([]);
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [addOnOpen, setAddOnOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [bulkProductOptions, setBulkProductOptions] = useState<any[]>([]);
  const [relatedServiceOptions, setRelatedServiceOptions] = useState<any[]>([]);
  const [providerOptions, setProviderOptions] = useState<any[]>([]);
  const [depotOptions, setDepotOptions] = useState<any[]>([]);
  const [selectedDepot, setSelectedDepot] = useState<any>(null);
  const [selectedAddons, setSelectedAddons] = useState<any[]>([]);
  const [submittingRowId, setSubmittingRowId] = useState<string | null>(null);
  const columnHelper = createColumnHelper();
  const [editItemModalOpen, setEditItemModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [selectedService, setSelectedService] = useState<any>(null);
  const [selectedProvider, setSelectedProvider] = useState<any>(null);
  const [canOpenEditModal, setCanOpenEditModal] = useState(false);
  const [selectedBulkProduct, setSelectedBulkProduct] = useState<any>(null);
  const [newAddons, setNewAddons] = useState<any[]>([]);
  const [discount, setDiscount] = useState<any>(null);
  const { user } = useContext<any>(UserContext);
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Project Invoice";
    _init();
  }, []);

  const _init = async () => {
    const res = await getProject({ data: { id } });

    if (res.id) {
      setProject(res);
    }
    const bulkProducts = await getProducts({ query: { filters: { addon: "1:isnot" } } });
    setBulkProductOptions(bulkProducts.items.map((p: any) => ({ value: p.id, label: p.name })));

    const services = await getServices({});
    setRelatedServiceOptions(services.items.map((s: any) => ({ value: s.id, label: s.name })));

    const quoteRes = await getQuote({ data: { id: quoteId } });

    if (quoteRes.id) {
      setItems(quoteRes.items);
    }

    if (quoteRes.discountAmount) {
      const percent = quoteRes.discount ? quoteRes.discount.amount : 0; // assuming percent is stored as .amount
      const discountObj = {
        amount: quoteRes.discountAmount,
        total: quoteRes.discountTotal,
        discountName: quoteRes.discount.name,
        discountId: quoteRes.discount.id,
        discountPercent: percent,
      };
      setDiscount(discountObj);
    }
  };

  const submitInvoice = async (values: any) => {
    const transformedItems = items.map((group) =>
      group.map((item: any) => {
        if (item.type === "service" && item.quantity === 0) {
          return { ...item, quantity: 1 };
        }
        return item;
      })
    );

    const invoiceData = {
      project: { id: id },
      contactsCC: values.contactsCC,
      primaryClient: { id: values.primaryClient },
      projectName: values.projectName,
      quote: { id: quoteId },
      client: { id: project.client.id },
      company: { id: user.company.id },
      lineitems: transformedItems,
      applyDiscount: discount ? true : false,
      discount: discount ? {id: discount.discountId} : undefined,
      discountAmount: discount ? discount.amount : undefined,
      discountTotal: discount ? discount.total : undefined,
    };

    const res = await createInvoice({ data: invoiceData });
    if (res.id) {
      void trackActivity({
        module: "invoices",
        action: "create_invoice",
        actionType: "create",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "invoice",
        entityId: res.id,
        metadata: {
          projectId: id || null,
          quoteId: quoteId || null,
        },
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      toast.add(`Invoice created.`, "var(--theme-ui-colors-success)");
      // 7b: return to the WO invoice tab when opened from a work order,
      // else the new invoice record as before.
      navigate(
        returnToWo
          ? `/maintenance/work-orders/${returnToWo}?tab=invoice`
          : `/invoices/${res.id}`,
      );
    }
  };

  const _validate = Yup.object().shape({
    projectName: Yup.string().required("Project name is required."),
  });

  const toggleItemModal = () => {
    if (itemModalOpen) {
      setProviderOptions([]);
      setDepotOptions([]);
    }
    setItemModalOpen(!itemModalOpen);
  };

  const submitItemAdd = async (values: any) => {
    console.log("submit item add", values);
  };

  const submitItemEdit = async (values: any) => {
    const productItem = selectedRow.find((i: any) => i.type === "product");
    const newItem = await transformEditedItem(values);

    setItems((prevItems) =>
      prevItems.map((group) => {
        const groupEntityIds = group
          .map((item: any) => item.entityId)
          .sort()
          .join(",");
        const selectedEntityIds = selectedRow
          .map((item: any) => item.entityId)
          .sort()
          .join(",");
        return groupEntityIds === selectedEntityIds ? newItem[0] : group;
      })
    );

    toggleEditItemModal();
  };

  const transformEditedItem = async (values: any) => {
    const result: any[] = [];
    const productItem = selectedRow.find((i: any) => i.type === "product");
    const serviceItem = selectedRow.find((i: any) => i.type === "service");

    const depotRes = await getDepot({ data: { id: values.depot }, provider: { id: values.provider } });

    // Bulk product
    if (values.bulkProduct) {
      const bulkProductRes = await getProduct({ data: { id: values.bulkProduct } });
      result.push({
        id: productItem.id,
        name: bulkProductRes.name || "", // optional
        description: bulkProductRes.description,
        cost: values.unitCost || 0,
        markup: values.markup || 0,
        price: Math.round((values.unitCost || 0) * (1 + (values.markup || 0) / 100) * 100) / 100, // rounded to 2 decimals
        unit: bulkProductRes.unit || "",
        entityId: values.bulkProduct,
        position: 0,
        type: "product",
        itemGroup: 0,
        depot: depotRes,
        quantity: values.quantity,
      });
    }

    // Addons
    if (Array.isArray(values.addons) && values.addons.length > 0) {
      values.addons.forEach((addon: any) => {
        result.push({
          id: addon.id || undefined,
          name: addon.name,
          description: addon.description || null,
          quantity: addon.quantity || 0,
          cost: addon.cost || 0,
          markup: addon.markup || 0,
          price: Math.round((addon.cost || 0) * (1 + (addon.markup || 0) / 100) * 100) / 100, // rounded to 2 decimals
          unit: addon.unit || "",
          entityId: addon.entityId,
          position: addon.position || 0,
          type: "addon-product",
          itemGroup: addon.itemGroup || 0,
          depot: addon.depot,
        });
      });
    }

    // Related service
    if (values.relatedService) {
      const serviceRes = await getService({ data: { id: values.relatedService } });
      result.push({
        id: serviceItem.id,
        name: serviceRes.name,
        description: serviceRes.description,
        quantity: 0,
        cost: 0,
        markup: 0,
        price: 0,
        unit: "yds", // or whatever the service uses
        entityId: values.relatedService,
        position: 0,
        type: "service",
        itemGroup: 0,
        depot: depotRes,
      });
    }

    return [result]; // nested array
  };

  const toggleAddOnModal = () => {
    setSearchTerm("");
    setAddOnOpen(!addOnOpen);
  };

  const getMatchingDepots = async (combinedIds: string, serviceIds: string, providerId?: string) => {
    const depotRes = await getAllDepots({ query: { joins: ["products", "services"], filters: { $or: { "products.id": `${combinedIds}:in`, "services.id": `${serviceIds}:in` } } } });
    if (depotRes.items) {
      const uniqueProviders = Array.from(new Map(depotRes.items.map((item: any) => [item.provider.id, { label: item.provider.name, value: item.provider.id }])).values());

      setProviderOptions(uniqueProviders);

      const uniqueDepots = depotRes.items
        .filter((depot: any) => depot.provider.id === providerId)
        .map((depot: any) => ({
          value: depot.id,
          label: depot.name,
        }));

      setDepotOptions(uniqueDepots);
    }
  };

  const calculateTotalPrice = (item: any) => {
    const productItem = item.find((i: any) => i.type === "product");
    const addonItems = item.filter((i: any) => i.type === "addon-product");
    const mainServiceTotal = productItem.quantity * parseFloat(productItem.price);
    const addonsTotal = addonItems.reduce((sum: number, addon: any) => sum + (addon.quantity > 0 ? addon.quantity * parseFloat(addon.price) : 0), 0);
    return formatCurrency(mainServiceTotal + addonsTotal);
  };

  const calculateTotalCost = (item: any) => {
    const productItem = item.find((i: any) => i.type === "product");
    const addonItems = item.filter((i: any) => i.type === "addon-product");
    const mainServiceTotal = productItem.quantity * parseFloat(productItem.price);
    const addonsTotal = addonItems.reduce((sum: number, addon: any) => sum + (addon.quantity > 0 ? addon.quantity * parseFloat(addon.price) : 0), 0);
    const totalPrice = mainServiceTotal + addonsTotal;
    const markupAmount = (totalPrice * parseFloat(productItem.markup)) / 100;
    return formatCurrency(totalPrice - markupAmount);
  };

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString("en-US", { style: "currency", currency: "USD" });
  };

  const columns = [
    columnHelper.accessor((row: any) => row?.name, {
      id: "id",
      cell: (info: any) => (
        <Span color="#000" fontWeight="500">
          {info.getValue()}
        </Span>
      ),
      header: () => <span>Name</span>,
      meta: {
        style: { width: "200px" },
      },
    }),
    columnHelper.accessor((row: any) => row?.quantity, {
      id: "quantity",
      cell: (info: any) => {
        const quantity = info.getValue();
        const unit = info.row.original?.unit;
        return quantity !== undefined ? `${quantity} ${unit || ""}`.trim() : "";
      },
      header: () => <span>Quantity</span>,
    }),
    columnHelper.accessor((row: any) => row?.markup, {
      id: "markup",
      cell: (info: any) => `${info.getValue()}%`,
      header: () => <span>Markup</span>,
    }),
    columnHelper.accessor((row: any) => row?.cost, {
      id: "baseCost",
      cell: (info: any) => `$${info.getValue()}`,
      header: () => <span>Cost Per Unit</span>,
    }),

    columnHelper.accessor((row: any) => row?.price, {
      id: "price",
      cell: (info: any) => `$${info.getValue()}`,
      header: () => <span>Price Per Unit</span>,
    }),
    columnHelper.accessor((row: any) => row?.cost * row?.quantity, {
      id: "totalCost",
      cell: (info: any) => formatCurrency(info.getValue()),
      header: () => <span>Total Cost</span>,
    }),
    columnHelper.accessor((row: any) => row?.price * row?.quantity, {
      id: "totalPrice",
      cell: (info: any) => formatCurrency(info.getValue()),
      header: () => <span>Total Price</span>,
    }),
  ];

  const toggleEditItemModal = (item?: any) => {
    if (item) {
      const productItem = item.find((i: any) => i.type === "product");
      const serviceItem = item.find((i: any) => i.type === "service");
      const addonItems = item.filter((i: any) => i.type === "addon-product");
      const bulkProductId = productItem.id;
      const addOnIds = addonItems.map((addon: any) => addon.addonId);
      const combinedProductIds = [bulkProductId, ...addOnIds].join(",");

      getDepotDetails(productItem.depot.id, productItem.depot.provider.id, combinedProductIds);

      setSelectedRow(item);
      setSelectedBulkProduct(productItem);
      setSelectedAddons(addonItems);
      setSelectedService(serviceItem.entityId);
      setSelectedProvider(productItem.depot.provider.id);
      setSelectedDepot(productItem.depot.id);
      setCanOpenEditModal(true);
    } else {
      setSelectedRow(null);
      setSelectedDepot(null);
      setSelectedProvider(null);
      setSelectedService(null);
      setEditItemModalOpen(false);
      setSelectedBulkProduct(null);
      setCanOpenEditModal(false);
    }
  };

  useEffect(() => {
    if (canOpenEditModal && selectedRow && selectedDepot && selectedBulkProduct) {
      setEditItemModalOpen(true);
      setCanOpenEditModal(false); // Reset flag after opening
    }
  }, [canOpenEditModal, selectedRow, selectedDepot, selectedBulkProduct]);

  const getDepotDetails = async (depotId: string, providerId: string, combinedProductIds?: any) => {
    const depotRes = await getDepot({ data: { id: depotId }, provider: { id: providerId } });

    if (depotRes.id) {
      setSelectedDepot(depotRes);

      const serviceOptions = depotRes.services.map((service: any) => ({
        value: service.id,
        label: service.name,
      }));

      setDepotOptions(serviceOptions);
      const combinedServiceIds = (depotRes.services || []).map((service: any) => service.id);
      if (combinedProductIds) {
        getMatchingDepots(combinedProductIds, combinedServiceIds.join(","), providerId);
      }
    }
  };

  const removeDiscount = () => {
    setDiscount(null);
  };

  const recalculateDiscount = (items: any[], discountPercent: number) => {
    // Flatten items if needed
    const allItems = items.flat ? items.flat() : items.reduce((acc, group) => acc.concat(group), []);
    // Calculate total (sum of price * quantity for all items)
    const total = allItems.reduce((sum: number, item: any) => sum + (item.price * item.quantity), 0);
    const discountAmount = parseFloat(((total * discountPercent) / 100).toFixed(2));
    const discountTotal = parseFloat((total - discountAmount).toFixed(2));
    return { discountAmount, discountTotal, total };
  };

  useEffect(() => {
    console.log("Items changed, recalculating discount if applicable");
    console.log("Current discount state:", discount);
    if (discount && discount.amount && discount.discountPercent) {
      // If you store the percent, use it; otherwise, calculate from amount/total
      const percent = discount.discountPercent;
      const { discountAmount, discountTotal } = recalculateDiscount(items, percent);
      console.log("Recalculated discount amount:", discountAmount, "Total after discount:", discountTotal);
      setDiscount((prev: any) => ({
        ...prev,
        amount: discountAmount,
        total: discountTotal,
      }));
    }
  }, [items]);

  return (
    <>
      <Header></Header>
      {!project && (
        <Box sx={{ textAlign: "center", paddingTop: "30px" }}>
          <Spinner size={25} strokeWidth={3} title="loading" />
        </Box>
      )}
      {project && (
        <Page
          title="Create Invoice"
          subtitle="Add an invoice to your existing project."
          backAction={
            <Link variant="back" href={backHref}>
              <BackArrowIcon />
              {returnToWo ? `Back to work order` : `Back to Project`}
            </Link>
          }
        >
          <Formik initialValues={{ projectName: project.projectName, primaryClient: project.primaryClient.id, contactsCC: project.contactsCC }} onSubmit={submitInvoice} validationSchema={_validate}>
            {({ isSubmitting, errors, values, submitCount, setFieldValue, handleSubmit }) => (
              <Form noValidate autoComplete="off">
                <Tabs
                  vertical
                  stickyFooter
                  footer={
                    <Flex sx={{ gap: "10px", flexGrow: 1, justifyContent: "flex-end" }}>
                      <Link href={`/projects/${id}`} sx={{ ...themevals.buttons.secondaryOutline, textDecoration: "none", margin: "0 10px 0 0" }}>
                        Cancel
                      </Link>
                      <BasicButton type="submit" $submitting={isSubmitting} styles={{ margin: "0 10px 0 0" }} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
                        {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}
                        Save Invoice
                      </BasicButton>
                    </Flex>
                  }
                >
                  <Tab itemKey="general">
                    <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
                      <Box>
                        <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                          Basic Info
                        </H5>
                        <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                          The project name is readonly and generated by the project you are attaching this invoice to.
                        </Subtext>
                      </Box>
                      <Flex sx={{ flex: 1 }}>
                        <TextInput
                          name="projectName"
                          label="Project Name"
                          value={values.projectName}
                          onChange={(val: any) => setFieldValue("projectName", val)}
                          $errors={errors.projectName && submitCount > 0 ? errors.projectName : null}
                          $customStyles={{ flex: 1, marginBottom: "0px" }}
                          readonly={true}
                        />
                      </Flex>
                    </Grid>
                    <Separator />
                    <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
                      <Box>
                        <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                          Client Info
                        </H5>
                        <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                          Contact information is readonly and is generated by the project you are attaching this invoice to.
                        </Subtext>
                      </Box>
                      <Grid gap={"20px"} columns={[1, "1fr 1fr"]}>
                        <Flex sx={{ flex: 1 }}>
                          <SelectInput
                            name="primaryClient"
                            label={"Primary Client"}
                            value={values.primaryClient}
                            options={[{ label: project.primaryClient.name, value: project.primaryClient.id }]}
                            onChange={(val: any) => setFieldValue("primaryClient", val)}
                            $errors={errors.primaryClient && submitCount > 0 ? errors.primaryClient : null}
                            $customStyles={{ width: "100%" }}
                            disabled={true}
                          />
                        </Flex>
                        <Flex sx={{ flex: 1 }}>
                          <MultiText
                            name="contactsCC"
                            label="CC"
                            value={values.contactsCC}
                            onChange={(val: string[]) => setFieldValue("contactsCC", val)}
                            $errors={errors.contactsCC && submitCount > 0 ? errors.contactsCC : null}
                            $customStyles={{ width: "100%" }}
                            disabled={true}
                          />
                        </Flex>
                      </Grid>
                    </Grid>
                    <Separator />
                    <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
                      <Box>
                        <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                          Items
                        </H5>
                        <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                          Add products and services needed on the quote.
                        </Subtext>
                        <Box sx={{ marginTop: "20px" }}>
                          <BasicButton scheme="secondaryOutline" type="button" onClick={() => toggleItemModal()}>
                            <PlusIcon /> Add item
                          </BasicButton>
                        </Box>
                      </Box>
                      <Flex>
                        {items.length > 0 ? (
                          <Flex sx={{ flexDirection: "column", gap: "10px", width: "100%" }}>
                            {items.map(function (item: any, index: number) {
                              const productItem = item.find((i: any) => i.type === "product");
                              const serviceItem = item.find((i: any) => i.type === "service");
                              const addonItems = item.filter((i: any) => i.type === "addon-product");
                              return (
                                <Flex
                                  key={index}
                                  sx={{
                                    flexDirection: "column",
                                    border: `1px solid ${themevals.colors.inputOutline}`,
                                    borderRadius: "6px",
                                    boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07);",
                                    width: "100%",
                                  }}
                                >
                                  <Flex sx={{ flexDirection: "row", padding: "15px 20px 15px 20px", borderBottom: `1px solid ${themevals.colors.inputOutline}` }}>
                                    <Flex sx={{ flex: 1, alignItems: "center" }}>
                                      <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000", marginRight: "5px" }}>{productItem.name}</Box>
                                      <Box>-</Box>
                                      <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "500", color: themevals.colors.dkgrey, marginLeft: "5px" }}>{serviceItem.name}</Box>
                                    </Flex>
                                    <Flex sx={{ flex: 1, justifyContent: "flex-end" }}>
                                      <BasicButton
                                        type="button"
                                        $submitting={submittingRowId === item.id}
                                        styles={{ margin: "0 0 0 10px" }}
                                        $iconHeight="14px"
                                        $iconWidth="14px"
                                        $loadingColor="#fff"
                                        keepText={true}
                                        onClick={() => toggleEditItemModal(item)}
                                        scheme={"secondaryOutline"}
                                      >
                                        {submittingRowId !== item.id && <PencilIcon fill={"#000"} />}
                                        Edit
                                      </BasicButton>
                                    </Flex>
                                  </Flex>
                                  <Grid gap={"30px"} columns={[1, "1fr 1fr 1fr 1fr"]} sx={{ padding: "20px" }}>
                                    <Flex sx={{ flexDirection: "column" }}>
                                      <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.dkgrey, marginBottom: "8px", lineHeight: "12px" }}>Quantity</Box>
                                      <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000" }}>
                                        {productItem.quantity} {productItem.unit}
                                      </Box>
                                    </Flex>
                                    <Flex sx={{ flexDirection: "column" }}>
                                      <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.dkgrey, marginBottom: "8px", lineHeight: "12px" }}>Markup</Box>
                                      <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000" }}>{productItem.markup}%</Box>
                                    </Flex>
                                    <Flex sx={{ flexDirection: "column" }}>
                                      <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.dkgrey, marginBottom: "8px", lineHeight: "12px" }}>
                                        Bulk Product Cost Per Unit
                                      </Box>
                                      <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000" }}>${productItem.cost}</Box>
                                    </Flex>
                                    <Flex sx={{ flexDirection: "column" }}>
                                      <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.dkgrey, marginBottom: "8px", lineHeight: "12px" }}>
                                        Bulk Product Price Per Unit
                                      </Box>
                                      <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000" }}>${productItem.price}</Box>
                                    </Flex>
                                    <Flex sx={{ flexDirection: "column" }}>
                                      <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.dkgrey, marginBottom: "8px", lineHeight: "12px" }}>
                                        Grand Total Cost
                                      </Box>
                                      <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000" }}>{calculateTotalCost(item)}</Box>
                                    </Flex>
                                    <Flex sx={{ flexDirection: "column" }}>
                                      <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.dkgrey, marginBottom: "8px", lineHeight: "12px" }}>
                                        Grand Total Price
                                      </Box>
                                      <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000" }}>{calculateTotalPrice(item)}</Box>
                                    </Flex>
                                    <Flex sx={{ flexDirection: "column" }}>
                                      <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.dkgrey, marginBottom: "8px", lineHeight: "12px" }}>Provider</Box>
                                      <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000" }}>{productItem.depot.provider.name}</Box>
                                    </Flex>
                                    <Flex sx={{ flexDirection: "column" }}>
                                      <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.dkgrey, marginBottom: "8px", lineHeight: "12px" }}>Depot</Box>
                                      <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000" }}>{productItem.depot.name}</Box>
                                    </Flex>
                                  </Grid>
                                  {addonItems.some((addon: any) => addon.quantity > 0) && (
                                    <Flex sx={{ flexDirection: "column", padding: "20px" }}>
                                      <H5 fontFamily="Poppins" fontSize="12px" marginLeft="0px" marginTop="0px" marginBottom="0px" color={themevals.colors.dkgrey}>
                                        Add-on Products
                                      </H5>
                                      <TableBasic
                                        data={addonItems}
                                        columns={columns}
                                        enablePaging={false}
                                        enableSearch={false}
                                        customStyles={{ border: `1px solid ${themevals.colors.inputOutline}` }}
                                      />
                                    </Flex>
                                  )}
                                </Flex>
                              );
                            })}
                          </Flex>
                        ) : (
                          <Flex>
                            <Subtext fontSize={"14px"} fontWeight={"400"} fontFamily={"Poppins"}>
                              No items have been added to the invoice.
                            </Subtext>
                          </Flex>
                        )}
                      </Flex>
                    </Grid>
                    <Separator />
                    <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
                      <Box>
                        <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                          Discounts
                        </H5>
                        <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                          Manage discounts associated with an invoice.
                        </Subtext>
                      </Box>
                      {discount ? (
                        <Grid gap={"20px"} columns={[1, "1fr 1fr"]}>
                          <Flex
                            sx={{
                              flexDirection: "column",
                              border: `1px solid ${themevals.colors.inputOutline}`,
                              borderRadius: "6px",
                              boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07);",
                              width: "100%",
                            }}
                          >
                            <Flex sx={{ flexDirection: "row", padding: "15px 20px 15px 20px", borderBottom: `1px solid ${themevals.colors.inputOutline}` }}>
                              <Flex sx={{ flex: 1, alignItems: "center" }}>
                                <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000", marginRight: "5px" }}>{discount.discountName}</Box>
                              </Flex>
                              <Flex sx={{ flex: 1, justifyContent: "flex-end" }}>
                                      <BasicButton
                                        type="button"
                                        
                                        styles={{ margin: "0 0 0 10px" }}
                                        $iconHeight="14px"
                                        $iconWidth="14px"
                                        $loadingColor="#fff"
                                        keepText={true}
                                        onClick={() => removeDiscount()}
                                        scheme={"secondaryOutline"}
                                      >
                                        <TrashIcon fill={"#000"} />
                                        Remove
                                      </BasicButton>
                                    </Flex>
                            </Flex>
                            <Grid gap={"30px"} columns={[1, "1fr 1fr"]} sx={{ padding: "20px" }}>
                              <Flex sx={{ flexDirection: "column" }}>
                                <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.dkgrey, marginBottom: "8px", lineHeight: "12px" }}>Discount Amount</Box>
                                <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000" }}>
                                  {discount?.amount !== undefined && discount?.amount !== null
                                    ? Number(discount.amount).toLocaleString("en-US", { style: "currency", currency: "USD" })
                                    : "N/A"}
                                </Box>
                              </Flex>
                              <Flex sx={{ flexDirection: "column" }}>
                                <Box sx={{ fontFamily: "Poppins", fontSize: "12px", fontWeight: "500", color: themevals.colors.dkgrey, marginBottom: "8px", lineHeight: "12px" }}>Discount Total</Box>
                                <Box sx={{ fontFamily: "Poppins", fontSize: "14px", fontWeight: "600", color: "#000" }}>
                                  {discount?.total !== undefined && discount?.total !== null
                                    ? Number(discount.total).toLocaleString("en-US", { style: "currency", currency: "USD" })
                                    : "N/A"}
                                </Box>
                              </Flex>
                            </Grid>
                          </Flex>
                        </Grid>
                      ) : (
                        <Flex sx={{ flex: 1, alignItems: "flex-start", justifyContent: "flex-start", padding: "20px 20px 20px 0" }}>
                          <Subtext fontSize={"14px"} fontWeight={"400"} fontFamily={"Poppins"}>
                            No discounts have been applied to this invoice.
                          </Subtext>
                        </Flex>
                      )}
                    </Grid>
                  </Tab>
                </Tabs>
              </Form>
            )}
          </Formik>
          <Modal isOpen={itemModalOpen} toggle={() => toggleItemModal()} centered size={"md"}>
            <Formik
              initialValues={{
                addons: [],
              }}
              onSubmit={(values) => submitItemAdd(values)}
            >
              {({
                isSubmitting,
                values,
                setFieldValue,
                errors,
                submitCount,
              }: {
                isSubmitting: boolean;
                submitCount: number;
                values: any;
                setFieldValue: (name: string, val?: any) => void;
                errors: any;
              }) => {
                const handleProviderChange = (val: any, setFieldValue: any, fieldName: string) => {
                  setFieldValue(fieldName, val);
                  if (values.bulkProduct && values.relatedService) {
                    getMatchingDepots(values.bulkProduct, values.relatedService, val);
                  }
                };

                // Watch for changes to bulkProduct and relatedService and call getMatchingDepots
                useEffect(() => {
                  if (values.bulkProduct && values.relatedService) {
                    getMatchingDepots(values.bulkProduct, values.relatedService);
                  }
                }, [values.bulkProduct, values.relatedService]);

                const submitRemoveAddonItem = (addonToRemove: any) => {
                  const updated = values.addons.map((addon: any) => (addon.addonId === addonToRemove.addonId ? { ...addon, quantity: 0 } : addon));
                  setFieldValue("addons", updated);
                  setSelectedAddons(updated);
                };

                // Watch for changes to provider and depot and call getDepot
                useEffect(() => {
                  const fetchDepot = async () => {
                    if (values.provider && values.depot) {
                      const res = await getDepot({ data: { id: values.depot }, provider: { id: values.provider } });
                      if (res?.id) {
                        setSelectedDepot(res);
                      }
                    }
                  };
                  fetchDepot();
                }, [values.provider, values.depot]);

                const availableAddons =
                  selectedDepot?.products?.filter((product: any) => {
                    if (!product.addon) return false;
                    const existing = selectedAddons.find((addon: any) => addon.addonId === product.id);
                    return !existing || existing.quantity === 0;
                  }) ?? [];

                return (
                  <Form noValidate autoComplete="off">
                    <ModalHeader toggle={() => toggleItemModal()}>
                      <ModalIcon>
                        <ProductsIcon fill={"000"} />
                      </ModalIcon>
                      <ModalTitleWrapper>
                        <ModalTitle>Add Quote item</ModalTitle>
                        <ModalSubtitle>{"Add an item to the quote."}</ModalSubtitle>
                      </ModalTitleWrapper>
                    </ModalHeader>
                    <ModalBody>
                      <Flex sx={{ gap: "20px", flexDirection: "row" }}>
                        <Box sx={{ flex: 1 }}>
                          <SelectInput
                            name="bulkProduct"
                            label="Bulk Product"
                            value={values.bulkProduct}
                            options={bulkProductOptions}
                            $errors={errors.bulkProduct && submitCount > 0 ? errors.bulkProduct : null}
                            $customStyles={{ flexGrow: 1, width: "100%" }}
                            onChange={(val: any) => setFieldValue("bulkProduct", val)}
                          />
                        </Box>

                        <Box sx={{ flex: 1 }}>
                          <SelectInput
                            name="relatedService"
                            label="Related Service"
                            value={values.relatedService}
                            options={relatedServiceOptions}
                            $errors={errors.relatedService && submitCount > 0 ? errors.relatedService : null}
                            $customStyles={{ flexGrow: 1, width: "100%" }}
                            onChange={(val: any) => setFieldValue("relatedService", val)}
                          />
                        </Box>
                      </Flex>

                      <Flex sx={{ gap: "20px", marginTop: "30px" }}>
                        <Box sx={{ flex: 1 }}>
                          <SelectInput
                            name="provider"
                            label="Provider"
                            value={values.provider}
                            options={providerOptions}
                            $errors={errors.provider && submitCount > 0 ? errors.provider : null}
                            $customStyles={{ flexGrow: 1, width: "100%" }}
                            onChange={(val: any) => handleProviderChange(val, setFieldValue, "provider")}
                          />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <SelectInput
                            name="depot"
                            label="Depot"
                            value={values.depot}
                            options={depotOptions}
                            $errors={errors.depot && submitCount > 0 ? errors.depot : null}
                            $customStyles={{ flexGrow: 1, width: "100%" }}
                            onChange={(val: any) => setFieldValue("depot", val)}
                          />
                        </Box>
                      </Flex>

                      <Flex sx={{ gap: "20px", marginTop: "30px" }}>
                        <Box sx={{ flex: 1 }}>
                          <TextInput
                            name="unitCost"
                            label="Unit Cost"
                            type="number"
                            value={values.unitCost}
                            onChange={(val: any) => setFieldValue("unitCost", val)}
                            prefix={"$"}
                            $errors={errors?.unitCost?.base_cost && submitCount > 0 ? errors.unitCost : null}
                          />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <TextInput
                            name="quantity"
                            label="Quantity"
                            type="number"
                            value={values.quantity}
                            onChange={(val: any) => setFieldValue("quantity", val)}
                            $errors={errors?.quantity && submitCount > 0 ? errors.quantity : null}
                          />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <TextInput
                            name="markup"
                            label="Markup"
                            type="number"
                            value={values.markup}
                            onChange={(val: any) => setFieldValue("markup", val)}
                            $errors={errors?.markup && submitCount > 0 ? errors.markup : null}
                            suffix={"%"}
                          />
                        </Box>
                      </Flex>

                      <Flex>
                        <Subtext fontFamily="Poppins" marginTop="0px" marginBottom="8px">
                          Add-on Products
                        </Subtext>
                      </Flex>
                      <Flex sx={{ border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "6px", flexDirection: "column", marginBottom: "30px" }}>
                        <Flex sx={{ padding: "20px", gap: "20px", flexDirection: "column" }}>
                          {values.addons.filter((addon: any) => addon.quantity > 0).length > 0 &&
                            values.addons.map((addon: any, index: number) =>
                              addon.quantity > 0 ? (
                                <Flex
                                  key={index}
                                  sx={{
                                    gap: "10px",
                                    justifyContent: "flex-start",
                                    borderRadius: "5px",
                                    border: `1px solid ${themevals.colors.mdgrey}`,
                                    padding: "10px 15px",
                                    width: "100%",
                                  }}
                                >
                                  <Flex sx={{ flex: 1, alignItems: "center", justifyContent: "flex-start" }}>
                                    <P fontFamily="Poppins" marginTop="0px" marginBottom="0px" fontSize="14px" fontWeight="500">
                                      {addon.name}
                                    </P>
                                  </Flex>
                                  <Flex sx={{ alignItems: "center", justifyContent: "flex-end", flex: 1, gap: "20px" }}>
                                    <Flex sx={{ alignItems: "center", justifyContent: "flex-end" }}>
                                      <TextInput
                                        name={`addons[${index}].baseCost`}
                                        value={addon.baseCost}
                                        type="number"
                                        label="Base Cost"
                                        prefix={"$"}
                                        $customStyles={{ marginBottom: "0px" }}
                                        onChange={(val: any) => setFieldValue(`addons[${index}].baseCost`, val)}
                                        $errors={errors?.addons?.[index].baseCost && submitCount > 0 ? errors.unitCost : null}
                                      />
                                    </Flex>

                                    <Flex sx={{ alignItems: "center", justifyContent: "flex-end" }}>
                                      <TextInput
                                        name={`addons[${index}].quantity`}
                                        value={addon.quantity}
                                        type="number"
                                        label={"Quantity"}
                                        min={1}
                                        suffix={addon.unit}
                                        $customStyles={{ marginBottom: "0px" }}
                                        onChange={(val: any) => setFieldValue(`addons[${index}].quantity`, val)}
                                        $errors={errors?.addons?.[index].quantity && submitCount > 0 ? errors.quantity : null}
                                        onKeyDown={(e: any) => {
                                          if (e.key === "0" && (e.currentTarget.value === "" || e.currentTarget.value === "0")) {
                                            e.preventDefault();
                                          }
                                        }}
                                        onBlur={(e: any) => {
                                          const val = parseFloat(e.target.value);
                                          if (isNaN(val) || val < 1) {
                                            setFieldValue(`addons[${index}].quantity`, 1);
                                          }
                                        }}
                                      />
                                    </Flex>

                                    <Flex sx={{ alignItems: "center", justifyContent: "flex-end" }}>
                                      <TextInput
                                        name={`addons[${index}].markup`}
                                        value={addon.markup}
                                        type="number"
                                        label="Markup"
                                        suffix={"%"}
                                        $customStyles={{ marginBottom: "0px" }}
                                        onChange={(val: any) => setFieldValue(`addons[${index}].markup`, val)}
                                        $errors={errors?.addons?.[index].markup && submitCount > 0 ? errors?.addons?.[index].markup : null}
                                      />
                                    </Flex>
                                  </Flex>

                                  <Flex sx={{ alignItems: "flex-end", justifyContent: "flex-end", paddingLeft: "10px", alignSelf: "stretch", width: "30px" }}>
                                    <Flex sx={{ alignSelf: "stretch", alignItems: "flex-end", paddingBottom: "10px" }}>
                                      <Box sx={{ cursor: "pointer" }} onClick={() => submitRemoveAddonItem(addon)}>
                                        <TrashIcon fill={themevals.colors.dkgrey} width="14px" />
                                      </Box>
                                    </Flex>
                                  </Flex>
                                </Flex>
                              ) : null
                            )}
                          {values.addons.filter((addon: any) => addon.quantity > 0).length === 0 && (
                            <P fontFamily="Poppins" fontSize="14px" color={`${themevals.colors.dkgrey}`}>
                              There are no add-on products associated with this item.
                            </P>
                          )}
                        </Flex>
                        {availableAddons.length > 0 && (
                          <Flex sx={{ borderTop: `1px solid ${themevals.colors.inputOutline}`, padding: "20px", justifyContent: "flex-end" }}>
                            <BasicButton
                              type="button"
                              scheme="secondaryOutline"
                              $submitting={isSubmitting}
                              $iconHeight="14px"
                              $iconWidth="14px"
                              $loadingColor="#000"
                              keepText={true}
                              onClick={() => toggleAddOnModal()}
                            >
                              {!isSubmitting && <PlusIcon fill={"#000"} />}
                              Add product
                            </BasicButton>
                          </Flex>
                        )}
                      </Flex>
                    </ModalBody>
                    <ModalFooter>
                      <Button variant="secondaryOutline" type="button" onClick={() => toggleItemModal()} style={{ marginRight: "10px" }}>
                        Cancel
                      </Button>
                      <BasicButton type="submit" $submitting={isSubmitting} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
                        {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}
                        Save item
                      </BasicButton>
                    </ModalFooter>
                  </Form>
                );
              }}
            </Formik>
          </Modal>
          <Modal isOpen={addOnOpen} toggle={() => toggleAddOnModal()} centered size={"sm"}>
            <Formik
              initialValues={{ addons: selectedAddons }}
              onSubmit={() => {
                setAddOnOpen(false);
              }}
              enableReinitialize
            >
              {({
                isSubmitting,
                values,
                setFieldValue,
                submitCount,
                errors,
              }: {
                isSubmitting: boolean;
                values: any;
                setFieldValue: (name: string, val?: any) => void;
                submitCount: number;
                errors: any;
              }) => {
                const toggleSelectAll = () => {
                  if (newAddons.length === availableAddons.length) {
                    // If all are selected, deselect all
                    setNewAddons([]);
                  } else {
                    // Select all available add-ons
                    setNewAddons(
                      availableAddons.map((product: any) => ({
                        ...product,
                        addonId: product.id,
                        quantity: 1,
                        baseCost: product.baseCost,
                      }))
                    );
                  }
                };

                const handleAddSelectedAddons = () => {
                  setSelectedAddons((prevAddons) => [...prevAddons, ...newAddons]); // Merge selections into first modal
                  setNewAddons([]); // Clear temporary selections
                  setAddOnOpen(false); // Close the modal
                };

                const handleAddonSelect = (product: any) => {
                  const existing = selectedAddons.find((addon: any) => addon.addonId === product.id);
                  setNewAddons((prev) =>
                    prev.some((addon) => addon.id === product.id)
                      ? prev.filter((addon) => addon.entity !== product.id) // Remove if already selected
                      : [
                          ...prev,
                          {
                            ...product,
                            entityId: product.id,
                            quantity: 1,
                            baseCost: existing?.baseCost,
                            markup: existing?.markup,
                          },
                        ]
                  );
                };

                const availableAddons =
                  selectedDepot?.products?.filter((product: any) => {
                    if (!product.addon) return false;
                    const existing = selectedAddons.find((addon: any) => addon.entityId === product.id);
                    return !existing || existing.quantity === 0;
                  }) ?? [];

                return (
                  <Form noValidate autoComplete="off">
                    <ModalHeader toggle={() => toggleAddOnModal()}>
                      <ModalIcon>
                        <ProductsIcon fill={"000"} />
                      </ModalIcon>
                      <ModalTitleWrapper>
                        <ModalTitle>Add products</ModalTitle>
                        <ModalSubtitle>{"Add available products at this depot."}</ModalSubtitle>
                      </ModalTitleWrapper>
                    </ModalHeader>
                    <ModalBody>
                      <SearchField value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} width="100%" />
                      <Flex sx={{ marginTop: "20px", marginBottom: "10px", alignItems: "center", paddingLeft: "11px" }}>
                        <Checkbox label="Select All" onChange={toggleSelectAll} checked={newAddons.length === availableAddons.length && availableAddons.length > 0} />
                      </Flex>
                      {availableAddons.map((product: any, idx: any) => (
                        <Flex
                          key={product.id}
                          sx={{
                            gap: "1rem",
                            alignItems: "center",
                            marginBottom: "10px",
                            border: `1px solid ${values.addons.includes(product.id) ? "black" : themevals.colors.mdgrey}`,
                            borderRadius: "6px",
                            height: "38px",
                            padding: "0px 10px",
                          }}
                        >
                          <Checkbox label={product.name} checked={newAddons.some((addon) => addon.id === product.id)} onChange={() => handleAddonSelect(product)} labelSize="14px" />
                        </Flex>
                      ))}
                    </ModalBody>
                    <ModalFooter>
                      <Button variant="secondaryOutline" type="button" onClick={() => setAddOnOpen(false)} style={{ marginRight: "10px" }}>
                        Cancel
                      </Button>
                      <BasicButton type="button" onClick={() => handleAddSelectedAddons()} $submitting={isSubmitting} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
                        {!isSubmitting && <PlusIcon fill={"#fff"} />}
                        Add products
                      </BasicButton>
                    </ModalFooter>
                  </Form>
                );
              }}
            </Formik>
          </Modal>
          <Modal isOpen={editItemModalOpen} toggle={() => toggleEditItemModal()} centered size={"md"}>
            <Formik
              initialValues={{
                addons: selectedAddons,
                relatedService: selectedService,
                bulkProduct: selectedBulkProduct?.entityId,
                provider: selectedProvider,
                depot: selectedDepot,
                unitCost: selectedBulkProduct?.cost,
                quantity: selectedBulkProduct?.quantity,
                markup: selectedBulkProduct?.markup,
              }}
              onSubmit={(values) => submitItemEdit(values)}
            >
              {({
                isSubmitting,
                values,
                setFieldValue,
                errors,
                submitCount,
              }: {
                isSubmitting: boolean;
                submitCount: number;
                values: any;
                setFieldValue: (name: string, val?: any) => void;
                errors: any;
              }) => {
                const handleProviderChange = (val: any, setFieldValue: any, fieldName: string) => {
                  setFieldValue(fieldName, val);
                  if (values.bulkProduct && values.relatedService) {
                    getMatchingDepots(values.bulkProduct, values.relatedService, val);
                  }
                };

                const submitRemoveAddonItem = (addonToRemove: any) => {
                  const updated = values.addons.filter((addon: any) => addon.entityId !== addonToRemove.entityId);
                  setFieldValue("addons", updated);
                  setSelectedAddons(updated);
                };

                // Watch for changes to bulkProduct and relatedService and call getMatchingDepots
                useEffect(() => {
                  if (values.bulkProduct && values.relatedService) {
                    getMatchingDepots(values.bulkProduct, values.relatedService, values.provider);
                  }
                }, [values.bulkProduct, values.relatedService]);

                // Watch for changes to provider and depot and call getDepot
                useEffect(() => {
                  const fetchDepot = async () => {
                    if (values.provider && values.depot) {
                      const res = await getDepot({ data: { id: values.depot }, provider: { id: values.provider } });
                      if (res?.id) {
                        setSelectedDepot(res);
                      }
                    }
                  };
                  fetchDepot();
                }, [values.provider, values.depot]);

                useEffect(() => {
                  const mergedAddons = [...values.addons];

                  selectedAddons.forEach((newAddon) => {
                    const existingIndex = mergedAddons.findIndex((a) => a.entityId === newAddon.entityId);

                    if (existingIndex > -1) {
                      mergedAddons[existingIndex] = {
                        ...mergedAddons[existingIndex],
                        quantity: newAddon.quantity ?? mergedAddons[existingIndex].quantity,
                        markup: newAddon.markup ?? mergedAddons[existingIndex].markup,
                        baseCost: newAddon.cost ?? mergedAddons[existingIndex].cost,
                      };
                    } else {
                      mergedAddons.push({ ...newAddon, quantity: newAddon.quantity || 1 });
                    }
                  });

                  setFieldValue("addons", mergedAddons);
                }, [selectedAddons]);

                const availableAddons =
                  selectedDepot?.products?.filter((product: any) => {
                    if (!product.addon) return false;
                    const existing = selectedAddons.find((addon: any) => addon.entityId === product.id);
                    return !existing || existing.quantity === 0;
                  }) ?? [];

                return (
                  <Form noValidate autoComplete="off">
                    <ModalHeader toggle={() => toggleEditItemModal()}>
                      <ModalIcon>
                        <ProductsIcon fill={"000"} />
                      </ModalIcon>
                      <ModalTitleWrapper>
                        <ModalTitle>Edit Invoice item</ModalTitle>
                        <ModalSubtitle>{"Edit an item on the invoice."}</ModalSubtitle>
                      </ModalTitleWrapper>
                    </ModalHeader>
                    <ModalBody>
                      <Flex sx={{ gap: "20px", flexDirection: "row" }}>
                        <Box sx={{ flex: 1 }}>
                          <SelectInput
                            name="bulkProduct"
                            label="Bulk Product"
                            value={values.bulkProduct}
                            options={bulkProductOptions}
                            $errors={errors.bulkProduct && submitCount > 0 ? errors.bulkProduct : null}
                            $customStyles={{ flexGrow: 1, width: "100%" }}
                            onChange={(val: any) => setFieldValue("bulkProduct", val)}
                          />
                        </Box>

                        <Box sx={{ flex: 1 }}>
                          <SelectInput
                            name="relatedService"
                            label="Related Service"
                            value={values.relatedService}
                            options={relatedServiceOptions}
                            $errors={errors.relatedService && submitCount > 0 ? errors.relatedService : null}
                            $customStyles={{ flexGrow: 1, width: "100%" }}
                            onChange={(val: any) => setFieldValue("relatedService", val)}
                          />
                        </Box>
                      </Flex>

                      <Flex sx={{ gap: "20px", marginTop: "30px" }}>
                        <Box sx={{ flex: 1 }}>
                          <SelectInput
                            name="provider"
                            label="Provider"
                            value={values.provider}
                            options={providerOptions}
                            $errors={errors.provider && submitCount > 0 ? errors.provider : null}
                            $customStyles={{ flexGrow: 1, width: "100%" }}
                            onChange={(val: any) => handleProviderChange(val, setFieldValue, "provider")}
                          />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <SelectInput
                            name="depot"
                            label="Depot"
                            value={values.depot}
                            options={depotOptions}
                            $errors={errors.depot && submitCount > 0 ? errors.depot : null}
                            $customStyles={{ flexGrow: 1, width: "100%" }}
                            onChange={(val: any) => setFieldValue("depot", val)}
                          />
                        </Box>
                      </Flex>

                      <Flex sx={{ gap: "20px", marginTop: "30px" }}>
                        <Box sx={{ flex: 1 }}>
                          <TextInput
                            name="unitCost"
                            label="Unit Cost"
                            type="number"
                            value={values.unitCost}
                            onChange={(val: any) => setFieldValue("unitCost", val)}
                            prefix={"$"}
                            $errors={errors?.unitCost?.base_cost && submitCount > 0 ? errors.unitCost : null}
                          />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <TextInput
                            name="quantity"
                            label="Quantity"
                            type="number"
                            value={values.quantity}
                            onChange={(val: any) => setFieldValue("quantity", val)}
                            $errors={errors?.quantity && submitCount > 0 ? errors.quantity : null}
                          />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <TextInput
                            name="markup"
                            label="Markup"
                            type="number"
                            value={values.markup}
                            onChange={(val: any) => setFieldValue("markup", val)}
                            $errors={errors?.markup && submitCount > 0 ? errors.markup : null}
                            suffix={"%"}
                          />
                        </Box>
                      </Flex>

                      <Flex>
                        <Subtext fontFamily="Poppins" marginTop="0px" marginBottom="8px">
                          Add-on Products
                        </Subtext>
                      </Flex>
                      <Flex sx={{ border: `1px solid ${themevals.colors.inputOutline}`, borderRadius: "6px", flexDirection: "column", marginBottom: "30px" }}>
                        <Flex sx={{ padding: "20px", gap: "20px", flexDirection: "column" }}>
                          {values.addons.filter((addon: any) => addon.quantity > 0).length > 0 &&
                            values.addons.map((addon: any, index: number) =>
                              addon.quantity > 0 ? (
                                <Flex
                                  key={index}
                                  sx={{
                                    gap: "10px",
                                    justifyContent: "flex-start",
                                    borderRadius: "5px",
                                    border: `1px solid ${themevals.colors.mdgrey}`,
                                    padding: "10px 15px",
                                    width: "100%",
                                  }}
                                >
                                  <Flex sx={{ flex: 1, alignItems: "center", justifyContent: "flex-start" }}>
                                    <P fontFamily="Poppins" marginTop="0px" marginBottom="0px" fontSize="14px" fontWeight="500">
                                      {addon.name}
                                    </P>
                                  </Flex>
                                  <Flex sx={{ alignItems: "center", justifyContent: "flex-end", flex: 1, gap: "20px" }}>
                                    <Flex sx={{ alignItems: "center", justifyContent: "flex-end" }}>
                                      <TextInput
                                        name={`addons[${index}].cost`}
                                        value={addon.cost}
                                        type="number"
                                        label="Base Cost"
                                        prefix={"$"}
                                        $customStyles={{ marginBottom: "0px" }}
                                        onChange={(val: any) => setFieldValue(`addons[${index}].cost`, val)}
                                        $errors={errors?.addons?.[index].cost && submitCount > 0 ? errors?.addons?.[index].cost : null}
                                      />
                                    </Flex>

                                    <Flex sx={{ alignItems: "center", justifyContent: "flex-end" }}>
                                      <TextInput
                                        name={`addons[${index}].quantity`}
                                        value={addon.quantity}
                                        type="number"
                                        label={"Quantity"}
                                        min={1}
                                        suffix={addon.unit}
                                        $customStyles={{ marginBottom: "0px" }}
                                        onChange={(val: any) => setFieldValue(`addons[${index}].quantity`, val)}
                                        $errors={errors?.addons?.[index].quantity && submitCount > 0 ? errors?.addons?.[index].quantity : null}
                                        onKeyDown={(e: any) => {
                                          if (e.key === "0" && (e.currentTarget.value === "" || e.currentTarget.value === "0")) {
                                            e.preventDefault();
                                          }
                                        }}
                                        onBlur={(e: any) => {
                                          const val = parseFloat(e.target.value);
                                          if (isNaN(val) || val < 1) {
                                            setFieldValue(`addons[${index}].quantity`, 1);
                                          }
                                        }}
                                      />
                                    </Flex>

                                    <Flex sx={{ alignItems: "center", justifyContent: "flex-end" }}>
                                      <TextInput
                                        name={`addons[${index}].markup`}
                                        value={addon.markup}
                                        type="number"
                                        label="Markup"
                                        suffix={"%"}
                                        $customStyles={{ marginBottom: "0px" }}
                                        onChange={(val: any) => setFieldValue(`addons[${index}].markup`, val)}
                                        $errors={errors?.addons?.[index].markup && submitCount > 0 ? errors?.addons?.[index].markup : null}
                                      />
                                    </Flex>
                                  </Flex>

                                  <Flex sx={{ alignItems: "flex-end", justifyContent: "flex-end", paddingLeft: "10px", alignSelf: "stretch", width: "30px" }}>
                                    <Flex sx={{ alignSelf: "stretch", alignItems: "flex-end", paddingBottom: "10px" }}>
                                      <Box sx={{ cursor: "pointer" }} onClick={() => submitRemoveAddonItem(addon)}>
                                        <TrashIcon fill={themevals.colors.dkgrey} width="14px" />
                                      </Box>
                                    </Flex>
                                  </Flex>
                                </Flex>
                              ) : null
                            )}
                          {values.addons.filter((addon: any) => addon.quantity > 0).length === 0 && (
                            <P fontFamily="Poppins" fontSize="14px" color={`${themevals.colors.dkgrey}`}>
                              There are no add-on products associated with this item.
                            </P>
                          )}
                        </Flex>
                        {availableAddons.length > 0 && (
                          <Flex sx={{ borderTop: `1px solid ${themevals.colors.inputOutline}`, padding: "20px", justifyContent: "flex-end" }}>
                            <BasicButton
                              type="button"
                              scheme="secondaryOutline"
                              $submitting={isSubmitting}
                              $iconHeight="14px"
                              $iconWidth="14px"
                              $loadingColor="#000"
                              keepText={true}
                              onClick={() => toggleAddOnModal()}
                            >
                              {!isSubmitting && <PlusIcon fill={"#000"} />}
                              Add product
                            </BasicButton>
                          </Flex>
                        )}
                      </Flex>
                    </ModalBody>
                    <ModalFooter>
                      <Button variant="secondaryOutline" type="button" onClick={() => toggleEditItemModal()} style={{ marginRight: "10px" }}>
                        Cancel
                      </Button>
                      <BasicButton type="submit" $submitting={isSubmitting} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
                        {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}
                        Save item
                      </BasicButton>
                    </ModalFooter>
                  </Form>
                );
              }}
            </Formik>
          </Modal>
        </Page>
      )}
    </>
  );
};

export default ProjectInvoiceCreate;
